/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-22 08:46:15
 * @ Modified by: David Helmick
 * @ Modified time: 2025-10-24 18:08:02
 * @ Description: Dialog providing sharable links to see the pdf reports of the property
 */

import React, { useEffect, useState } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Grid,
  Button,
  Card,
  Dialog,
  Fab,
  InputAdornment,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Box,
} from '@mui/material'

import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import { enqueueSnackbar } from 'notistack'
import withSession from '@/housefolios-components/Session/withSession'
import { getProformaLink } from '@/utils/proforma'
import { getStrategyName } from '@/utils/stratagies'
import { ON_DARK } from '@/theme/skinSx'

function SharePDF(props) {
  const { propertyId, handleShareClose } = props

  const hostname = import.meta.env.VITE_HOSTNAME

  const [type, setType] = useState(props.type || 'buyHold')

  useEffect(() => {
    setType(props.type || 'buyHold')
  }, [props.type])

  const proformaLink = getProformaLink(propertyId, [type])
  const previewLink = getProformaLink(propertyId, [type], true)

  const encodedProformaLink = encodeURIComponent(proformaLink)

  const [modal1, setModal1] = useState(false)

  const toggle1 = () => {
    setModal1(!modal1)
    handleShareClose()
  }

  return (
    <>
      <ListItemButton key="share" onClick={toggle1} sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        width: "100%",
        cursor: "pointer",
        columnGap: "8px",
        "& > .MuiSvgIcon-root": { marginLeft: "auto", flexShrink: 0 },
      }}>
        <Box sx={{
          display: "flex",
          alignItems: "center",
          columnGap: "8px",
          minWidth: 0,
          flex: "1 1 auto",
          justifyContent: "flex-start",
          "& > .MuiBox-root:first-of-type": {
            flex: "0 0 auto",
            width: 16,
            minWidth: 16,
            marginRight: 0,
          },
        }}>
          <Box sx={{
            width: 16,
            minWidth: 16,
            flexShrink: 0,
            textAlign: "center",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 10,
            fontSize: "1.15rem",
            "& > svg": { width: "1em", height: "1em" },
            ".Mui-selected > &": { opacity: "1 !important" },
          }}>
            <FontAwesomeIcon icon={['fas', 'share-nodes']} />
          </Box>
          <span>Share Proforma</span>
        </Box>
      </ListItemButton>
      <Dialog
        scroll="body"
        maxWidth="lg"
        open={modal1}
        onClose={toggle1}
        slotProps={{ paper: { sx: {
          borderRadius: "0.65rem !important",
          border: 0,
          backgroundColor: "#fff",
          p: "1rem",
          "@media (min-width: 1440px)": { p: 0 },
          [ON_DARK]: {
            backgroundColor: '#1e1e1e !important',
            color: 'rgba(255, 255, 255, 0.87)',
          },
        } } }}
      >
        <Grid container spacing={0}>
          <Grid size={12} container>
            <Grid>
              <Box sx={{
                "@media (min-width: 1440px)": { mt: 0 },
                mt: "1.5rem",
              }}>
                <Box sx={{ m: '1.5rem' }}>
                  <Box
                    component="h1"
                    sx={[
                      { my: '1rem', fontWeight: 700 },
                      { fontSize: '1.7rem', fontWeight: '300', lineHeight: '1.2' },
                      {
                        [ON_DARK]: {
                          color: 'rgba(255, 255, 255, 0.87) !important',
                        },
                      },
                    ]}
                  >
                    Share Your Report
                  </Box>
                  <Box
                    component="p"
                    sx={[
                      { mb: 0, color: 'rgba(7, 9, 25, 0.5)' },
                      { fontSize: '1rem !important' },
                      {
                        [ON_DARK]: {
                          color: 'rgba(255, 255, 255, 0.68) !important',
                        },
                      },
                    ]}
                  >
                    Share the link to your report over social media!
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Select
                    variant="outlined"
                    margin="dense"
                    value={type}
                    onChange={(event) => setType(event.target.value)}
                  >
                    <MenuItem key="buyhold" value={'buyHold'}>
                      <ListItemText
                        primary={getStrategyName(
                          'Buy & Hold',
                          props?.session?.me?.activeOrg?.member,
                        )}
                      />
                    </MenuItem>
                    <MenuItem key="fixFlip" value={'fixFlip'}>
                      <ListItemText
                        primary={getStrategyName(
                          'Fix & Flip',
                          props?.session?.me?.activeOrg?.member,
                        )}
                      />
                    </MenuItem>
                    <MenuItem
                      key="marketing"
                      value={'marketing'}
                    >
                      <ListItemText
                        primary={
                          props?.session?.me?.activeOrg
                            ?.southernImpressions
                            ? getStrategyName(
                                'Build to Rent',
                                props?.session?.me?.activeOrg?.member,
                              )
                            : 'End Buyer'
                        }
                      />
                    </MenuItem>
                    <MenuItem
                      key="shortTermRental"
                      value={'shortTermRental'}
                    >
                      <ListItemText
                        primary={getStrategyName(
                          'Short Term Rental',
                          props?.session?.me?.activeOrg?.member,
                        )}
                      />
                    </MenuItem>
                    {props?.session?.me?.activeOrg?.member !==
                      'astroflip' && (
                      <MenuItem key="wholesale" value={'wholesale'}>
                        <ListItemText
                          primary={getStrategyName(
                            'Wholesale',
                            props?.session?.me?.activeOrg?.member,
                          )}
                        />
                      </MenuItem>
                    )}
                    {props?.session?.me?.activeOrg?.member ===
                      'astroflip' && (
                      <MenuItem key="astroflip" value={'astroflip'}>
                        <ListItemText
                          primary={getStrategyName(
                            'Astroflip',
                            props?.session?.me?.activeOrg?.member,
                          )}
                        />
                      </MenuItem>
                    )}
                  </Select>
                </Box>
                <Card
                  sx={{
                    boxShadow: 'none',
                    backgroundColor: 'transparent',
                    p: '1.5rem',
                    border: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'block',
                      p: '1rem',
                      pt: 0,
                      borderRadius: '0.65rem',
                      backgroundColor: '#f4f5fd',
                      [ON_DARK]: {
                        backgroundColor: '#2a2a2a !important',
                        color: 'rgba(255, 255, 255, 0.87)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        color: '#a6a6b9',
                        textAlign: 'center',
                        mb: '1rem',
                        [ON_DARK]: {
                          color: 'rgba(255, 255, 255, 0.68) !important',
                        },
                      }}
                    >
                      <small>Click below to share!</small>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Grid container direction="row" spacing={1}>
                        <Grid>
                          <Button
                           
                            target="_blank"
                            href={
                              'https://www.facebook.com/sharer/sharer.php?u=' +
                              encodedProformaLink
                            }
                           sx={{
                             boxShadow: "0 0.25rem 0.55rem rgba(59, 89, 153, 0.35)",
                             color: "#fff",
                             backgroundColor: "#3b5999",
                             backgroundImage: "none",
                             borderColor: "#3b5999",
                             "&:not(:disabled):hover": { backgroundColor: "#3b5999", color: "#fff" },
                             "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#344e87" },
                             "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(59, 89, 153, 0.4), 0 0.0625rem 0.385rem rgba(59, 89, 153, 0.54)" },
                             "&:not(:disabled):not(.active):active": { boxShadow: "0 0.22rem 0.525rem rgba(59, 89, 153, 0.4), 0 0.0625rem 0.385rem rgba(59, 89, 153, 0.54)" },
                           }}>
                            <span style={{ display: "inline-flex" }}>
                              <FontAwesomeIcon
                                icon={['fab', 'facebook']}
                              />
                            </span>
                            <Box component="span" sx={{
                              display: "inline-block",
                              "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                              "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
                            }}>
                              Facebook
                            </Box>
                          </Button>
                        </Grid>
                        <Grid>
                          <Button
                           
                            href={
                              'https://twitter.com/intent/tweet?text=' +
                              encodedProformaLink
                              //'I%27ve%20been%20using%20Housefolios%20real%20estate%20software%20to%20analyze%20investment%20properties.%20Using%20this%20link%20will%20let%20you%20analyze%2010%20properties%20(and%20give%20me%2010%20more%20properties!)'
                            }
                            target="_blank"
                           sx={{
                             boxShadow: "0 0.25rem 0.55rem rgba(29, 161, 242, 0.35)",
                             color: "#fff",
                             backgroundColor: "#1da1f2",
                             backgroundImage: "none",
                             borderColor: "#1da1f2",
                             "&:not(:disabled):hover": { backgroundColor: "#1da1f2", color: "#fff" },
                             "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#0d95e8" },
                             "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(29, 161, 242, 0.4), 0 0.0625rem 0.385rem rgba(29, 161, 242, 0.54)" },
                             "&:not(:disabled):not(.active):active": { boxShadow: "0 0.22rem 0.525rem rgba(29, 161, 242, 0.4), 0 0.0625rem 0.385rem rgba(29, 161, 242, 0.54)" },
                           }}>
                            <span style={{ display: "inline-flex" }}>
                              <FontAwesomeIcon
                                icon={['fab', 'twitter']}
                              />
                            </span>
                            <Box component="span" sx={{
                              display: "inline-block",
                              "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                              "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
                            }}>
                              Twitter
                            </Box>
                          </Button>
                        </Grid>
                        <Grid>
                          <Button
                           
                            href={
                              'https://www.linkedin.com/sharing/share-offsite/?url=' +
                              encodedProformaLink
                            }
                            target="_blank"
                           sx={{
                             boxShadow: "0 0.25rem 0.55rem rgba(29, 161, 242, 0.35)",
                             color: "#fff",
                             backgroundColor: "#1da1f2",
                             backgroundImage: "none",
                             borderColor: "#1da1f2",
                             "&:not(:disabled):hover": { backgroundColor: "#1da1f2", color: "#fff" },
                             "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#0d95e8" },
                             "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(29, 161, 242, 0.4), 0 0.0625rem 0.385rem rgba(29, 161, 242, 0.54)" },
                             "&:not(:disabled):not(.active):active": { boxShadow: "0 0.22rem 0.525rem rgba(29, 161, 242, 0.4), 0 0.0625rem 0.385rem rgba(29, 161, 242, 0.54)" },
                           }}>
                            <span style={{ display: "inline-flex" }}>
                              <FontAwesomeIcon
                                icon={['fab', 'linkedin']}
                              />
                            </span>
                            <Box component="span" sx={{
                              display: "inline-block",
                              "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                              "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
                            }}>
                              LinkedIn
                            </Box>
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                  <div>
                    <Box
                      sx={{
                        textAlign: 'center',
                        color: '#a6a6b9',
                        my: '1rem',
                        [ON_DARK]: {
                          color: 'rgba(255, 255, 255, 0.68) !important',
                        },
                      }}
                    >
                      <small>Or use your unique link below!</small>
                    </Box>
                    <Box sx={{ mb: '1rem' }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        id="textfield-email"
                        value={proformaLink}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <CopyToClipboard
                                  text={proformaLink}
                                  onCopy={() =>
                                    enqueueSnackbar(
                                      `Copied to Clipboard`,
                                      {
                                        variant: 'success',
                                        autoHideDuration: 3000,
                                      },
                                    )
                                  }
                                >
                                  <Fab size="small" color="primary">
                                    <FileCopyOutlinedIcon
                                      style={{ color: 'white' }}
                                    />
                                  </Fab>
                                </CopyToClipboard>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Box>
                    <Grid
                      container
                      direction="row"
                      spacing={1}
                      sx={{
                        justifyContent: 'space-between',
                      }}
                    >
                      <Grid>
                        <Button
                          variant="contained"
                          color="primary"
                          style={{ color: 'white' }}

                          target="_blank"
                          href={previewLink}
                          sx={{
                            fontWeight: 700,
                            px: '1.5rem',
                            my: '1rem',
                          }}
                        >
                          Preview
                        </Button>
                      </Grid>
                      <Grid>
                        <Button
                          variant="contained"
                          color="primary"
                          style={{ color: 'white' }}
                          onClick={toggle1}

                          sx={{
                            fontWeight: 700,
                            px: '1.5rem',
                            my: '1rem',
                          }}
                        >
                          Close
                        </Button>
                      </Grid>
                    </Grid>
                  </div>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Dialog>
    </>
  )
}

export default withSession(SharePDF)
